
import { Component, Ref, Vue } from 'vue-property-decorator'
import NoticeListLayout from '@/components/templates/NoticeListLayout.vue'
import { NotificationType } from '@/const/NotificationType'
import { SelectItem } from '@/models/SelectItem'
import { NoticeMessageData } from '@/models/NoticeMessage'
import { NotificationsApi } from '@/api/methods/NotificationsApi'

/**
 * お知らせ一覧ページ
 */
@Component({
  components: {
    NoticeListLayout,
  },
})
export default class NoticeList extends Vue {
  /** お知らせページのレイアウト */
  @Ref()
  noticeListLayout!: NoticeListLayout

  /** 1ページの最大件数 */
  private readonly MAX_PER_PAGE = 10

  /** ページ番号の初期値 */
  private readonly DEFAULT_PAGE_NUMBER = 1

  /** お知らせ一覧 */
  private messageList: NoticeMessageData[] = []

  /** ページ数 */
  private pageLength = 0

  /** 現在のページ(初期値) */
  private currentPage = 1

  /**
   * クエリパラメータから取得したお知らせタイプ
   *
   * @returns お知らせタイプコード
   */
  private get defaultNotificationTypeCode(): string {
    const notificationType = this.$route.query.notificationType

    if (!notificationType || typeof notificationType !== 'string') {
      // 未指定時、string型でない場合は「全て」を設定
      return NotificationType.ALL.code
    }

    // お知らせタイプの存在チェック
    const notification = NotificationType.findByCode(notificationType)

    return notification?.code || NotificationType.ALL.code
  }

  /**
   * クエリパラメータから取得したページ番号
   *
   * @returns ページ番号
   */
  private get queryPageNumber(): string | undefined {
    const pageNumber = this.$route.query.pageNumber

    if (!pageNumber || typeof pageNumber !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return pageNumber
  }

  /**
   * 選択中のお知らせタイプ
   *
   * @returns お知らせタイプのコード
   */
  private get currentNotificationTypeCode(): string {
    // undefinedケース
    if (!this.noticeListLayout) {
      return this.defaultNotificationTypeCode
    }
    return this.noticeListLayout.currentNotificationTypeCode()
  }

  /**
   * お知らせタイプのリスト
   *
   * @returns SelectItem型のリスト
   */
  private get notificationTypeList(): SelectItem[] {
    const list: SelectItem[] = []
    NotificationType.LIST.forEach((item) => {
      list.push({ value: item.code, label: item.label })
    })
    return list
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // ページ番号をセット
    this.currentPage = Number(this.queryPageNumber) || this.DEFAULT_PAGE_NUMBER

    await this.getNotifications()

    // クエリが存在しない場合、クエリパラメータを更新
    if (Object.keys(this.$route.query).length === 0) {
      await this.replaceQueryParams()
    }

    this.$loading.complete()
  }

  /**
   * ページリンク押下時の処理
   *
   * @param value ページ番号/戻る/進む
   */
  private async changePage(value: number): Promise<void> {
    this.$loading.start()

    this.currentPage = value
    await this.getNotifications()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * クエリパラメータの更新
   */
  private async replaceQueryParams(): Promise<void> {
    await this.$router.replace({
      query: {
        notificationType: this.currentNotificationTypeCode,
        pageNumber: String(this.currentPage),
      },
    })
  }

  /**
   * プルダウンフィルター変更時の処理
   */
  private async changeFilter(): Promise<void> {
    this.$loading.start()

    this.currentPage = this.DEFAULT_PAGE_NUMBER
    await this.getNotifications()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * 行クリック時の処理
   *
   * @param messageId メッセージのID
   */
  private eventClickElement(messageId: number): void {
    this.$router.push({
      path: `/student/notice/${messageId}`,
      query: this.$prevPage.generateQuery(),
    })
  }

  /**
   * お知らせ一覧を再取得する
   */
  private async getNotifications(): Promise<void> {
    const searchResult = await NotificationsApi.getNotifications(this.MAX_PER_PAGE, {
      type: this.currentNotificationTypeCode,
      pageNumber: this.currentPage,
    })

    // お知らせ件数からページ数を設定
    const divideNumber = Math.floor(searchResult.totalCount / this.MAX_PER_PAGE)
    this.pageLength = searchResult.totalCount % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

    // お知らせ情報を設定
    this.messageList = searchResult.notifications.map((noticeDetail) => {
      return {
        id: noticeDetail.id,
        type: noticeDetail.type,
        title: noticeDetail.title,
        startedDate: new Date(noticeDetail.startedAt * 1000),
        endedDate: new Date(noticeDetail.endedAt * 1000),
        description: noticeDetail.message,
        updatedUser: noticeDetail.updatedBy,
        updatedAt: new Date(noticeDetail.updatedAt * 1000),
      }
    })
  }
}
